import { apiGet, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, isHeaderPinned, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme, updateSelectedSymbolInDropdown } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

export class TraderverseKeyFinancialStatements {
  private container: HTMLElement;
  private config: WidgetConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any = null;
  private currentChart: any;
  private widgetResizeObserver: ResizeObserver | null = null;
  private lastMetricType: string = 'keyMetrics';
  private selectedKeys: string[] = [];
  private userHasChangedKeySelection: boolean = false;

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
    ];
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    this.attachStyles();
    this.setupEventListeners();
    await this.setupChart(this.config.symbol!);
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
let dataTheme = 'default-dark';

if (this.config.theme === 'white') {
  themeClass = 'whiteMode';
  dataTheme = 'white';
} else if (this.config.theme === 'black') {
  themeClass = 'blackMode';
  dataTheme = 'black';
} else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}

    const symbolOptions = this.fetchSymbols()
      .map(s => `<option value="${s.value}">${s.label}</option>`).join('');

    const widgetHtml = `
        <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title stock">${this.getTitleIconSvg()} Key Metrics</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <div class="item">
                <button class="filters-toggle" type="button"> <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg> </button>
              </div>
            </div>
          </div>
          <div class="inBody">

            <div class="optionsRow">
              ${this.config.hideSymbol ? '<div class="item dropStyle01"></div>' : `<div class="item dropStyle01">
                              <div class="trader-sm-card" id="selected-symbol">
                              ${getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol!, type: "stock", name: "" })}
                              </div>
                              <span id="symbol-dropdown-placeholder"></span>
                              </div>`}
              <span></span>
              
              <div class="filtersBox sideBar hidden">
              <div class="item">
              <select class="metric-type-select">
                  <option value="keyMetrics" selected>Key Metrics</option>
                  <option value="ratio">Financial Ratios</option>
                  <option value="keyMetricsTTM">Key Metrics TTM</option>
                  <option value="ratioTTM">Financial Ratios TTM</option>
                  <option value="highlights">Highlights</option>
                  <option value="overview">Overview</option>
              </select>
              </div>
                <div class="item">
                  <input type="text" class="key-search" placeholder="Search keys..." style="width: 100%; margin-bottom: 8px;" />
                </div>
                <div class="item checkBoxes checkBoxes02" style="flex: 0 0 100%;">
                </div>
              </div>
            </div>

            <div class="chartDv" id="keyMarticsContainer">
            
            </div>

          </div>
        </div>
    `;
    this.shadowRoot.innerHTML = widgetHtml;
    if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
      const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
      const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
      if (placeholder && dropdown) {
        placeholder.replaceWith(dropdown);
      }
    }
  }


  onSelectChange(stock: any) {
    this.changeSymbol(stock.symbol, stock);
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    dropdown?.addEventListener('change', e => {
      const value = (e.target as HTMLSelectElement).value;
      this.changeSymbol(value);
    });
    if (dropdown && this.config.symbol) dropdown.value = this.config.symbol;

    const metricDropdown = this.shadowRoot.querySelector('.metric-type-select') as HTMLSelectElement;
    if (metricDropdown) {
      metricDropdown.value = this.lastMetricType;
      metricDropdown.addEventListener('change', async (e) => {
        const value = (e.target as HTMLSelectElement).value;
        this.lastMetricType = value;
        this.selectedKeys = [];
        this.userHasChangedKeySelection = false;
        await this.setupChart(this.config.symbol!, value);
      });
    }

    const keySearch = this.shadowRoot.querySelector('.key-search') as HTMLInputElement;
    if (keySearch) {
      keySearch.addEventListener('input', () => this.updateDisplay());
    }

    const filtersToggle = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement;
    const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement;
    if (filtersToggle && filtersBox) {
      filtersToggle.addEventListener('click', () => {
        filtersBox.classList.toggle('hidden');
      });
    }
  }

  private async setupChart(symbol: string, metricType: string = 'keyMetrics'): Promise<void> {
    try {
      if (metricType === 'highlights') {
        const res = await apiGet({
          method: "POST",
          url: `${APIURL}/v1/overview/highlights`,
          body: { symbol },
        });
        if (!res.ok) throw new Error('API Error');
        const data = await res.json();
        this.currentData = data;
        this.updateDisplay();
        this.emit('dataUpdate', this.currentData);
        return;
      }

      if (metricType === 'overview') {
        const res = await apiGet({
          method: "POST",
          url: `${APIURL}/v1/overview`,
          body: { symbol },
        });
        if (!res.ok) throw new Error('API Error');
        const data = await res.json();
        this.currentData = data;
        this.updateDisplay();
        this.emit('dataUpdate', this.currentData);
        return;
      }

      // Facts routes (the FMP key stays on the server): latest annual record for the non-TTM views.
      const factsUrl: { [key: string]: string } = {
        'keyMetrics': `${APIURL}/v1/fmp/key-metrics?symbol=${symbol}&period=annual&limit=1`,
        'ratio': `${APIURL}/v1/fmp/financial-ratios?symbol=${symbol}&period=annual&limit=1`,
        'keyMetricsTTM': `${APIURL}/v1/financial/key-metrics-ttm?symbol=${symbol}`,
        'ratioTTM': `${APIURL}/v1/financial/ratios-ttm?symbol=${symbol}`
      };

      const res = await apiGet({
        url: factsUrl[metricType] || factsUrl.keyMetrics,
        method: "GET",
      });

      if (!res.ok) throw new Error('API Error');
      const json = await res.json();
      // Facts wraps rows in { data: [...] }; FMP returned the bare array.
      this.currentData = Array.isArray(json) ? json : Array.isArray(json?.data) ? json.data : [];

      this.updateDisplay();
      this.emit('dataUpdate', this.currentData);
    } catch (err) {
      console.error("Error fetching chart data:", err);
    }
  }

  private updateDisplay(): void {
    if (!this.currentData) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    if (this.lastMetricType === 'highlights') {
      this.renderHighlights(this.currentData.data);
      return;
    }
    if (this.lastMetricType === 'overview') {
      this.renderOverview(this.currentData);
      return;
    }
    this.renderKeyMatrics(this.currentData);
  }

  private formatKeyLabel(key: string): string {
    if (key === 'ReportedCurrency') return 'Reported Currency';
    return key
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  private getDisplayKeysFromRecord(record: any): Array<{ key: string; label: string }> {
    if (!record || typeof record !== 'object') return [];
    return Object.keys(record).map(key => ({ key, label: this.formatKeyLabel(key) }));
  }

  private populateKeyFilterBox(allKeys: Array<{ key: string; label: string }>, selectedKeys: string[]): void {
    const metricBox = this.shadowRoot.querySelector('.filtersBox .checkBoxes');
    if (!metricBox) return;
    const keySearch = this.shadowRoot.querySelector('.key-search') as HTMLInputElement;
    let filtered = allKeys;
    if (keySearch?.value.trim()) {
      const term = keySearch.value.trim().toLowerCase();
      filtered = allKeys.filter(m => m.label.toLowerCase().includes(term) || m.key.toLowerCase().includes(term));
    }
    const selectedObjs = selectedKeys.map(k => allKeys.find(m => m.key === k)).filter(Boolean) as { key: string; label: string }[];
    const displayKeys: { key: string; label: string }[] = [];
    const seen = new Set<string>();
    for (const m of [...selectedObjs, ...filtered]) {
      if (!seen.has(m.key)) { displayKeys.push(m); seen.add(m.key); }
    }
    const hasInvalidSelection = this.selectedKeys.length > 0 && !this.selectedKeys.every(k => allKeys.some(m => m.key === k));
    const useDefaults = (!this.selectedKeys.length && !this.userHasChangedKeySelection) || hasInvalidSelection;
    if (useDefaults) {
      this.selectedKeys = allKeys.slice(0, 4).map(m => m.key);
    }
    metricBox.innerHTML = displayKeys.map((m, i) => {
      const checked = this.selectedKeys.includes(m.key);
      return `<div class="checkBoxField"><input class="filter-checkbox" type="checkbox" value="${m.key}" id="key-${i}" ${checked ? 'checked' : ''}/><label for="key-${i}">${m.label} <i>✓</i></label></div>`;
    }).join('');
    metricBox.querySelectorAll('.filter-checkbox').forEach((cb: Element) => {
      (cb as HTMLInputElement).addEventListener('change', () => {
        this.userHasChangedKeySelection = true;
        const checkedKeys = Array.from(metricBox.querySelectorAll('.filter-checkbox:checked')).map((c: any) => c.value);
        this.selectedKeys = checkedKeys;
        this.updateDisplay();
      });
    });
  }

  private renderKeyMatrics(dataArr: any[]): void {
    const container = this.shadowRoot.querySelector('#keyMarticsContainer');
    if (!container || !Array.isArray(dataArr) || dataArr.length === 0) return;

    const data = dataArr[0];
    const allKeys = this.getDisplayKeysFromRecord(data);
    const hasInvalidSelection = this.selectedKeys.length > 0 && !this.selectedKeys.every(k => allKeys.some(m => m.key === k));
    const useDefaults = (!this.selectedKeys.length && !this.userHasChangedKeySelection) || hasInvalidSelection;
    if (useDefaults) {
      this.selectedKeys = allKeys.slice(0, 4).map(m => m.key);
    }
    this.populateKeyFilterBox(allKeys, this.selectedKeys);

    const keysToShow = this.selectedKeys.filter(k => data.hasOwnProperty(k));
    if (keysToShow.length === 0) {
      container.innerHTML = getNoDataHTML();
      return;
    }

    const formatValue = (value: any): string => {
      if (typeof value === 'number') {
        if (Math.abs(value) >= 1_000_000_000_000)
          return (value / 1_000_000_000_000).toFixed(2) + 'T';
        if (Math.abs(value) >= 1_000_000_000)
          return (value / 1_000_000_000).toFixed(2) + 'B';
        if (Math.abs(value) >= 1_000_000)
          return (value / 1_000_000).toFixed(2) + 'M';
        if (Math.abs(value) >= 1_000)
          return (value / 1_000).toFixed(2) + 'K';
        return value.toFixed(2);
      }
      return value ?? '-';
    };

    const colors = ['orange', 'blue', 'seagreen'];

    let html = `<div class="keyMetricsMain">`;
    keysToShow.forEach((key, index) => {
      const color = colors[index % colors.length];
      const value = data[key];
      html += `
            <div class="item ${color}" data-tooltip="${this.formatKeyLabel(key)}">
                <p>${this.formatKeyLabel(key)}</p>
                <h5>${formatValue(value)}</h5>
            </div>
        `;
    });
    html += `</div>`;
    container.innerHTML = html;
  }

  private renderOverview(data: any): void {
    const container = this.shadowRoot.querySelector('#keyMarticsContainer');
    if (!container) return;

    const raw = Array.isArray(data?.data) ? data.data[0] : data?.data ?? data;
    if (!raw || typeof raw !== 'object') {
      container.innerHTML = getNoDataHTML();
      return;
    }

    const allKeys = this.getDisplayKeysFromRecord(raw);
    const hasInvalidSelection = this.selectedKeys.length > 0 && !this.selectedKeys.every(k => allKeys.some(m => m.key === k));
    const useDefaults = (!this.selectedKeys.length && !this.userHasChangedKeySelection) || hasInvalidSelection;
    if (useDefaults) {
      this.selectedKeys = allKeys.slice(0, 4).map(m => m.key);
    }
    this.populateKeyFilterBox(allKeys, this.selectedKeys);

    const keysToShow = this.selectedKeys.filter(k => raw.hasOwnProperty(k));
    if (keysToShow.length === 0) {
      container.innerHTML = getNoDataHTML();
      return;
    }

    const formatValue = (value: any): string => {
      if (value == null) return '-';
      if (typeof value === 'number') {
        if (Math.abs(value) >= 1_000_000_000_000) return (value / 1_000_000_000_000).toFixed(2) + 'T';
        if (Math.abs(value) >= 1_000_000_000) return (value / 1_000_000_000).toFixed(2) + 'B';
        if (Math.abs(value) >= 1_000_000) return (value / 1_000_000).toFixed(2) + 'M';
        if (Math.abs(value) >= 1_000) return (value / 1_000).toFixed(2) + 'K';
        return value.toFixed(2);
      }
      return String(value);
    };
    const colors = ['orange', 'blue', 'seagreen'];

    let html = '<div class="keyMetricsMain">';
    keysToShow.forEach((key, index) => {
      const color = colors[index % colors.length];
      const value = raw[key];
      html += `<div class="item ${color}" data-tooltip="${this.formatKeyLabel(key)}"><p>${this.formatKeyLabel(key)}</p><h5>${formatValue(value)}</h5></div>`;
    });
    html += '</div>';
    container.innerHTML = html;
  }

  private static HIGHLIGHT_KEYS: Array<{ key: string; label: string }> = [
    { key: 'symbol', label: 'Symbol' },
    { key: 'sentiment', label: 'Sentiment' },
    { key: 'market_range', label: 'Market Range' },
    { key: 'web_url', label: 'Company Website' },
    { key: 'total_volume', label: 'Total Volume' },
    { key: 'forward_dividend', label: 'Forward Dividend' },
    { key: 'ex_dividend_date', label: 'Ex-Dividend Date' },
    { key: 'earning_date', label: 'Earnings Date' }
  ];

  private renderHighlights(dataArr: any[]): void {
    const container = this.shadowRoot.querySelector('#keyMarticsContainer');
    if (!container) return;

    if (!Array.isArray(dataArr) || dataArr.length === 0) {
      container.innerHTML = getNoDataHTML();
      return;
    }

    const data = dataArr[0];
    const hasInvalidSelection = this.selectedKeys.length > 0 && !this.selectedKeys.every(k => TraderverseKeyFinancialStatements.HIGHLIGHT_KEYS.some(m => m.key === k));
    const useDefaults = (!this.selectedKeys.length && !this.userHasChangedKeySelection) || hasInvalidSelection;
    if (useDefaults) {
      this.selectedKeys = TraderverseKeyFinancialStatements.HIGHLIGHT_KEYS.slice(0, 4).map(m => m.key);
    }
    this.populateKeyFilterBox(TraderverseKeyFinancialStatements.HIGHLIGHT_KEYS, this.selectedKeys);

    const keysToShow = this.selectedKeys.filter(k => TraderverseKeyFinancialStatements.HIGHLIGHT_KEYS.some(m => m.key === k));
    if (keysToShow.length === 0) {
      container.innerHTML = getNoDataHTML();
      return;
    }

    const formatUrl = (url: string) => (url || '').replace(/^https?:\/\//, '');
    const formatHighlightValue = (key: string, value: any): string => {
      if (value == null) return 'N/A';
      if (key === 'web_url') return `<a href="${value}" target="_blank" style="word-break: break-all;">${formatUrl(value) || 'N/A'}</a>`;
      if (key === 'total_volume') return value?.toLocaleString() ?? 'N/A';
      return String(value);
    };

    const colors = ['orange', 'blue', 'seagreen'];
    let html = '<div class="keyMetricsMain">';
    keysToShow.forEach((key, index) => {
      const color = colors[index % colors.length];
      const label = TraderverseKeyFinancialStatements.HIGHLIGHT_KEYS.find(m => m.key === key)?.label ?? this.formatKeyLabel(key);
      const value = data[key];
      const display = key === 'web_url' ? formatHighlightValue(key, value) : (formatHighlightValue(key, value));
      const style = key === 'web_url' ? ' style="flex: 1 0 320px;"' : key === 'sentiment' || key === 'market_range' ? ' style="flex: 1 0 180px;"' : '';
      html += `<div class="item ${color}"${style}><p>${label}</p><h5>${display}</h5></div>`;
    });
    html += '</div>';
    container.innerHTML = html;
  }

  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(
      this.shadowRoot,
      this.config,
      stock,
      stockId,
      (stockId: any) => this.setupChart(stockId, this.lastMetricType),
      (type: any, data: any) => this.emit(type, data)
    );
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }
  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }
  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }
  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }
  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
  if (!this.shadowRoot) return;

  this.config.theme = toggleWidgetTheme(
    this.shadowRoot,
    theme
  ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

  this.updateDisplay();
}

  public destroy(): void {
    if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
    if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
    this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
  }
  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M8.59998 23.625C8.08748 23.625 7.66248 23.2 7.66248 22.6875V20.1C7.66248 19.5875 8.08748 19.1625 8.59998 19.1625C9.11248 19.1625 9.53748 19.5875 9.53748 20.1V22.6875C9.53748 23.2125 9.11248 23.625 8.59998 23.625Z" fill="currentColor"/> <path d="M15 23.625C14.4875 23.625 14.0625 23.2 14.0625 22.6875V17.5C14.0625 16.9875 14.4875 16.5625 15 16.5625C15.5125 16.5625 15.9375 16.9875 15.9375 17.5V22.6875C15.9375 23.2125 15.5125 23.625 15 23.625Z" fill="currentColor"/> <path d="M21.4 23.625C20.8875 23.625 20.4625 23.2 20.4625 22.6875V14.9125C20.4625 14.4 20.8875 13.975 21.4 13.975C21.9125 13.975 22.3375 14.4 22.3375 14.9125V22.6875C22.3375 23.2125 21.925 23.625 21.4 23.625Z" fill="currentColor"/> <path d="M8.59997 16.475C8.17497 16.475 7.79997 16.1875 7.68747 15.7625C7.56247 15.2625 7.86247 14.75 8.37497 14.625C12.975 13.475 17.025 10.9625 20.1125 7.37502L20.6875 6.70002C21.025 6.31252 21.6125 6.26252 22.0125 6.60002C22.4 6.93752 22.45 7.52502 22.1125 7.92502L21.5375 8.60002C18.2 12.5 13.8 15.2125 8.82497 16.45C8.74997 16.475 8.67497 16.475 8.59997 16.475Z" fill="currentColor"/> <path d="M21.4001 11.9C20.8876 11.9 20.4626 11.475 20.4626 10.9625V8.25H17.7375C17.225 8.25 16.8 7.825 16.8 7.3125C16.8 6.8 17.225 6.375 17.7375 6.375H21.4001C21.9126 6.375 22.3376 6.8 22.3376 7.3125V10.975C22.3376 11.4875 21.9251 11.9 21.4001 11.9Z" fill="currentColor"/> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> </svg>`;
  }

}

declare global { interface Window { TraderverseKeyFinancialStatements: typeof TraderverseKeyFinancialStatements; } }
if (typeof window !== 'undefined') { window.TraderverseKeyFinancialStatements = TraderverseKeyFinancialStatements; }
